import { ClientConfig } from '../models/ClientConfigMap';
import { AlmeConfig } from '../models/AlmeConfig';
import React, { ReactElement } from 'react';

import headerConfig, { AlmeHeaderConfig } from '../components/AlmeHeader/AlmeHeaderConfig';

import appConfig from '../components/AlmeApp/AlmeAppConfig';
import historyConfig, { AlmeHistoryConfig } from '../components/AlmeHistory/AlmeHistoryConfig';
import apiUtilsConfig from '../api/AlmeApiUtilsConfig';
import footerConfig, { AlmeFooterConfig } from '../components/AlmeFooter/AlmeFooterConfig';
import launchPointConfig, { AlmeLaunchPointConfig } from '../components/AlmeLaunchPoint/AlmeLaunchPointConfig';
import liveChatControlsConfig from '../components/LiveChat/AlmeLiveChatControls/AlmeLiveChatControlsConfig';
import themeModeToggleConfig from '../components/AlmeThemeModeToggle/AlmeThemeModeToggleConfig';
import launchToolsConfig from '../components/AlmeLaunchTools/AlmeLaunchToolsConfig';

import { FaRobot } from "react-icons/fa";
import { Avatar, ButtonProps, FilledTextFieldProps, OutlinedTextFieldProps, StandardTextFieldProps, TextFieldProps } from '@material-ui/core';
import chatRectangle from '../images/Chat_opt1.svg';
import chatCircle from '../images/Chat_opt2.svg';
import koala from '../images/Koala_opt1.svg';
import panda from '../images/Panda_opt1.svg';
import man from '../images/Person_opt1.svg';
import woman from '../images/Person_opt2.svg';
import person1 from '../images/Person_opt3.svg';
import person2 from '../images/Person_opt4.svg';
import { AlmeColorProp } from '../models/AlmeColorMapping';

const iconMap = {
    chatRectangle,
    chatCircle,
    koala,
    panda,
    man,
    woman,
    person1,
    person2,
} as Record<string, string>;

export function mapClientConfig(clientConfig: ClientConfig): AlmeConfig {
    const validatedConfig = getValidConfig(clientConfig);

    const headerConfigMapped = {
        ...headerConfig,
        header: {
            ...headerConfig.header,
        },
    } as AlmeHeaderConfig;

    const historyConfigMapped: AlmeHistoryConfig = {
        ...historyConfig,
        history: {
            ...historyConfig.history,
            response: {
                ...historyConfig.history.response,
                ivaResponse: {
                    ...historyConfig.history.response.ivaResponse,
                    ctaButton: {
                        styles: {
                            button: {
                                color: validatedConfig.callToActionColor,
                                variant: validatedConfig.callToActionVariant,
                            },
                        },
                    },
                    styles: {
                        displayLink: {
                            color: validatedConfig.displayLinkColor,
                            variant: validatedConfig.displayLinkVariant,
                        },
                        navUrl: {
                            color: validatedConfig.navUrlColor,
                            variant: validatedConfig.navUrlVariant,
                        },
                    },
                    ivaIcon: getIvaIcon(validatedConfig.ivaIcon || 'none'),
                },
            },
            scrollButton: {
                styles: {
                    ...historyConfig.history.scrollButton.styles,
                    color: validatedConfig.scrollButtonColor,
                },
            },
        },
    };

    const customFooterConfig: AlmeFooterConfig = {
        ...footerConfig,
        footer: {
            ...footerConfig.footer,
            input: {
                ...footerConfig.footer.input,
                styles: {
                    textField: {
                        color: validatedConfig.inputBoxColor,
                        variant: validatedConfig.inputBoxVariant,
                        size: validatedConfig.inputBoxSize,
                    },
                    button: {
                        color: validatedConfig.submitButtonColor,
                    },
                },
            },
        },
    };

    const customLaunchPointConfig: AlmeLaunchPointConfig = {
        ...launchPointConfig,
        launchPoint: {
            ...launchPointConfig.launchPoint,
            launchButton: {
                ...launchPointConfig.launchPoint.launchButton,
                color: validatedConfig.launchButtonColor || 'primary',
            },
        },
    };


    return {
        ...appConfig,
        ...apiUtilsConfig,
        ...headerConfigMapped,
        ...historyConfigMapped,
        ...customFooterConfig,
        ...customLaunchPointConfig,
        ...liveChatControlsConfig,
        ...themeModeToggleConfig,
        ...launchToolsConfig,
    } as AlmeConfig;
}

function getValidConfig(clientConfig: ClientConfig): ClientConfig {
    const validIconNames = Object.keys(iconMap).concat(['none', 'robot']);
    return {
        callToActionColor: getValidColor(clientConfig.callToActionColor, historyConfig.history.response.ivaResponse.ctaButton.styles.button.color),
        callToActionVariant: getValidVariant(clientConfig.callToActionVariant, historyConfig.history.response.ivaResponse.ctaButton.styles.button.variant),
        ivaIcon: validateString(clientConfig.ivaIcon, validIconNames) ? clientConfig.ivaIcon : 'robot',
        displayLinkColor: getValidColor(clientConfig.displayLinkColor, historyConfig.history.response.ivaResponse.styles.displayLink.color),
        displayLinkVariant: getValidVariant(clientConfig.displayLinkVariant, historyConfig.history.response.ivaResponse.styles.displayLink.variant),
        navUrlColor: getValidColor(clientConfig.navUrlColor, historyConfig.history.response.ivaResponse.styles.navUrl.color),
        navUrlVariant: getValidVariant(clientConfig.navUrlVariant, historyConfig.history.response.ivaResponse.styles.navUrl.variant),
        scrollButtonColor: getValidColor(clientConfig.scrollButtonColor, historyConfig.history.scrollButton.styles.color),
        inputBoxColor: getValidColor(clientConfig.inputBoxColor, footerConfig.footer.input.styles.textField.color, 'textField'),
        inputBoxVariant: getValidVariant(clientConfig.inputBoxVariant, footerConfig.footer.input.styles.textField.variant, true),
        inputBoxSize: getValidSize(clientConfig.inputBoxSize, footerConfig.footer.input.styles.textField.size),
        submitButtonColor: getValidColor(clientConfig.submitButtonColor, footerConfig.footer.input.styles.button.color),
        launchButtonColor: getValidColor(clientConfig.launchButtonColor, launchPointConfig.launchPoint.launchButton.color, 'colorProp'),
    } as ClientConfig;
}

function getIvaIcon(iconName: string): ReactElement | undefined {
    if (iconName === 'robot') {
        return (
            <Avatar>
                <FaRobot />
            </Avatar>
        );
    }
    if (iconName === 'none') {
        return;
    }
    return (<Avatar src={iconMap[iconName]}></Avatar>);
}

function getValidColor(color = "", defaultValue: ButtonProps['color'] | TextFieldProps['color'] | AlmeColorProp, type?: string): ButtonProps['color'] | TextFieldProps['color'] | AlmeColorProp {
    const validButtonColors = ['default', 'initial', 'primary', 'secondary'];
    const validSimpleColors = ['primary', 'secondary'];
    const validColorProps = ['default', 'divider', 'initial', 'paper', 'primary', 'secondary'];

    if (!type) {
        return validateString(color, validButtonColors) ? color as ButtonProps['color'] : defaultValue;
    }
    if (type === 'textField') {
        return validateString(color, validSimpleColors) ? color as TextFieldProps['color'] : defaultValue;
    }
    if (type === 'colorProp') {
        return validateString(color, validColorProps) ? color as AlmeColorProp : defaultValue;
    }
}

function getValidVariant(
    variant = "",
    defaultValue: ButtonProps['variant'] | FilledTextFieldProps['variant'] | OutlinedTextFieldProps['variant'] | StandardTextFieldProps['variant'],
    isTextField?: boolean)
    : ButtonProps['variant'] | FilledTextFieldProps['variant'] | OutlinedTextFieldProps['variant'] | StandardTextFieldProps['variant'] {
    const validButtonVariants = ['text', 'outlined', 'contained'];
    const validTextFieldVariants = ['filled', 'outlined', 'standard'];

    if (isTextField) {
        return validateString(variant, validTextFieldVariants) ?
            variant as FilledTextFieldProps['variant'] | OutlinedTextFieldProps['variant'] | StandardTextFieldProps['variant'] : defaultValue;
    }
    return validateString(variant, validButtonVariants) ? variant as ButtonProps['variant'] : defaultValue;
}

function getValidSize(size = "", defaultValue: TextFieldProps['size']): TextFieldProps['size'] {
    const validSizes = ['small', "medium"];
    return validateString(size, validSizes) ? size as TextFieldProps['size'] : defaultValue;
}

function validateString(str?: string, validStrings?: string[]): boolean {
    if (!str || typeof str !== "string" || str.length <= 0) {
        return false;
    }
    return validStrings ? validStrings.includes(str) : true;
}